import React, { useState } from 'react';
import {
  Container, Card, CardContent, TextField, Button, Typography,
  Box, Grid, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Checkbox, FormControlLabel
} from '@mui/material';
import { Business } from '@mui/icons-material';
import { submitBusinessApplication } from '../services/api';

const BUSINESS_TYPES = [
  'Recruitment/Staffing',
  'Corporate/Enterprise',
  'NGO/Non-Profit',
  'Education/Training',
  'Other'
];

const BusinessSignUpPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [formData, setFormData] = useState({
    'Business Name': '',
    'Contact Person': '',
    'Email': '',
    'Phone': '',
    'Business Type': '',
    'Reason for Access': ''
  });

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await submitBusinessApplication(formData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Application failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', p: 6 }}>
            <Business sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Application Submitted!
            </Typography>
            <Typography variant="body1" paragraph>
              Your business application has been submitted for review.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Our team will review your application and contact you soon.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Business Sign-Up Application
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        If you're a business seeking access to the alumni database, please fill out the form below.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={formData['Business Name']}
                  onChange={handleChange('Business Name')}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Person Name"
                  value={formData['Contact Person']}
                  onChange={handleChange('Contact Person')}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  value={formData.Email}
                  onChange={handleChange('Email')}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={formData.Phone}
                  onChange={handleChange('Phone')}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Type of Business</InputLabel>
                  <Select
                    value={formData['Business Type']}
                    onChange={handleChange('Business Type')}
                    label="Type of Business"
                  >
                    {BUSINESS_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason for Access"
                  value={formData['Reason for Access']}
                  onChange={handleChange('Reason for Access')}
                  multiline
                  rows={4}
                  required
                  helperText="Explain why you need access to the alumni database"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      required
                    />
                  }
                  label="I agree to the terms and conditions for accessing the alumni database"
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit Application'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default BusinessSignUpPage;
